/**
 * Hook for streaming collection creation with real-time progress updates via SSE
 */
import { useState, useCallback, useRef } from 'react';
import { Collection, CreateCollectionDTO } from '../types/collection';
import { getFunctionUrl } from '../config/v2FunctionUrls';
import { auth } from '../config/firebase';
import { getIdToken } from 'firebase/auth';

// Progress event from backend
export interface GenerationProgressEvent {
  total: number;
  leetcodeNumber: number;
  status: 'generating' | 'completed' | 'failed';
  completed: number;
  failed: number;
  title?: string;
  error?: string;
}

// Accumulated state for tracking parallel generation
export interface GenerationState {
  generating: number[];    // LeetCode numbers currently being generated
  completed: number;       // Count of completed generations
  failed: number;          // Count of failed generations
  total: number;           // Total problems to generate
}

export interface StreamState {
  isStreaming: boolean;
  generationState: GenerationState;
  error: string | null;
  collection: Collection | null;
}

export interface UseCollectionStreamResult {
  state: StreamState;
  startStream: (dto: CreateCollectionDTO) => Promise<Collection | null>;
  cancelStream: () => void;
}

// Helper to get auth headers using Firebase Auth
const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };

  // Get Firebase ID token if user is authenticated
  if (auth.currentUser) {
    try {
      const token = await getIdToken(auth.currentUser);
      headers['Authorization'] = `Bearer ${token}`;
    } catch (error) {
      console.error('Failed to get Firebase ID token:', error);
    }
  }

  return headers;
};

const initialGenerationState: GenerationState = {
  generating: [],
  completed: 0,
  failed: 0,
  total: 0
};

export function useCollectionStream(): UseCollectionStreamResult {
  const [state, setState] = useState<StreamState>({
    isStreaming: false,
    generationState: initialGenerationState,
    error: null,
    collection: null
  });

  const abortControllerRef = useRef<AbortController | null>(null);

  const cancelStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setState(prev => ({
      ...prev,
      isStreaming: false,
      generationState: initialGenerationState
    }));
  }, []);

  const startStream = useCallback(async (dto: CreateCollectionDTO): Promise<Collection | null> => {
    // Cancel any existing stream
    cancelStream();

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    setState({
      isStreaming: true,
      generationState: initialGenerationState,
      error: null,
      collection: null
    });

    return new Promise(async (resolve, reject) => {
      const headers = await getAuthHeaders();

      // Use fetch with streaming for SSE
      fetch(getFunctionUrl('createCollectionStream'), {
        method: 'POST',
        headers,
        body: JSON.stringify(dto),
        signal: abortControllerRef.current?.signal
      })
        .then(async (response) => {
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Request failed: ${response.status} - ${errorText}`);
          }

          const reader = response.body?.getReader();
          if (!reader) {
            throw new Error('No response body');
          }

          const decoder = new TextDecoder();
          let buffer = '';

          const processLine = (line: string) => {
            if (line.startsWith('event: ')) {
              // Store event type for next data line
              return;
            }

            if (line.startsWith('data: ')) {
              const jsonStr = line.slice(6);
              try {
                const data = JSON.parse(jsonStr);

                // Determine event type from data structure
                if (data.message === 'Starting collection creation') {
                  // Start event - initialize total from leetcodeNumbers
                  console.log('SSE: Start', data);
                  if (data.leetcodeNumbers) {
                    setState(prev => ({
                      ...prev,
                      generationState: {
                        ...prev.generationState,
                        total: data.leetcodeNumbers.length
                      }
                    }));
                  }
                } else if (data.status && data.leetcodeNumber !== undefined) {
                  // Progress event - update accumulated state
                  const progress = data as GenerationProgressEvent;

                  setState(prev => {
                    const newState = { ...prev.generationState };
                    newState.total = progress.total;
                    newState.completed = progress.completed;
                    newState.failed = progress.failed;

                    if (progress.status === 'generating') {
                      // Add to generating list if not already present
                      if (!newState.generating.includes(progress.leetcodeNumber)) {
                        newState.generating = [...newState.generating, progress.leetcodeNumber];
                      }
                    } else {
                      // Remove from generating list on completed/failed
                      newState.generating = newState.generating.filter(
                        num => num !== progress.leetcodeNumber
                      );
                    }

                    return {
                      ...prev,
                      generationState: newState
                    };
                  });
                } else if (data.success && data.data) {
                  // Complete event
                  const collection = data.data as Collection;
                  setState(prev => ({
                    ...prev,
                    isStreaming: false,
                    collection,
                    generationState: initialGenerationState
                  }));
                  resolve(collection);
                } else if (data.type === 'error' || data.type === 'usage') {
                  // Error event
                  setState(prev => ({
                    ...prev,
                    isStreaming: false,
                    error: data.message,
                    generationState: initialGenerationState
                  }));
                  reject(new Error(data.message));
                }
              } catch (e) {
                console.error('Failed to parse SSE data:', jsonStr, e);
              }
            }
          };

          const readStream = async (): Promise<void> => {
            while (true) {
              const { done, value } = await reader.read();

              if (done) {
                // Process any remaining buffer
                if (buffer.trim()) {
                  buffer.split('\n').forEach(processLine);
                }
                break;
              }

              buffer += decoder.decode(value, { stream: true });

              // Process complete lines
              const lines = buffer.split('\n');
              buffer = lines.pop() || ''; // Keep incomplete line in buffer

              lines.forEach(processLine);
            }
          };

          await readStream();
        })
        .catch((error) => {
          if (error.name === 'AbortError') {
            console.log('Stream cancelled');
            resolve(null);
            return;
          }

          console.error('Stream error:', error);
          setState(prev => ({
            ...prev,
            isStreaming: false,
            error: error.message,
            generationState: initialGenerationState
          }));
          reject(error);
        });
    });
  }, [cancelStream]);

  return {
    state,
    startStream,
    cancelStream
  };
}
